<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useConnectionHealth } from '../../composables/useConnectionHealth'
import { registerGlobalSurfaceMessages } from '../../i18n'

import { registerIntegrationsMessages, type AuthFamily } from '@/features/integrations'
import { useI18n, useLocaleStore } from '@/shared/i18n'
import { Alert, Button, Icon, PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui'

const MESSAGE_KEY = 'globalSurface.connection.lost'
const { t, te } = useI18n()
const localeStore = useLocaleStore()
const health = useConnectionHealth()
const messagesReady = ref(te(MESSAGE_KEY))

watch(
  () => localeStore.locale,
  async (locale) => {
    messagesReady.value = te(MESSAGE_KEY)
    try {
      await Promise.all([
        registerGlobalSurfaceMessages(locale),
        registerIntegrationsMessages(locale),
      ])
      messagesReady.value = true
    } catch {
      messagesReady.value = false
    }
  },
  { immediate: true },
)

const sections = computed(() => [
  ...(health.reconnectable.value.length
    ? [{ key: 'reconnectable' as const, platforms: health.reconnectable.value }]
    : []),
  ...(health.contactOwner.value.length
    ? [{ key: 'contactOwner' as const, platforms: health.contactOwner.value }]
    : []),
])

function platformLabel(platform: AuthFamily | undefined): string {
  return platform ? t(platform.nameKey) : ''
}

function lostMessage(platforms: readonly AuthFamily[]): string {
  return t(MESSAGE_KEY, {
    count: platforms.length,
    platform: platformLabel(platforms[0]),
  })
}

function contactMessage(platforms: readonly AuthFamily[]): string {
  return t('globalSurface.connection.contactOwner', {
    count: platforms.length,
    platform: platformLabel(platforms[0]),
  })
}
</script>

<template>
  <div v-if="messagesReady && sections.length" class="connection-lost-banner grid gap-2xs">
    <Alert v-for="section in sections" :key="section.key" tone="danger">
      <div
        class="connection-lost-banner__content flex flex-wrap items-center justify-center gap-sm"
      >
        <Icon name="alert-triangle" :size="20" aria-hidden="true" />
        <strong>{{ lostMessage(section.platforms) }}</strong>
        <div class="connection-lost-banner__platforms flex flex-wrap gap-2xs">
          <span
            v-for="platform in section.platforms"
            :key="platform.field"
            class="connection-lost-banner__platform grid w-8 h-8 place-items-center border border-danger rounded-md bg-surface"
          >
            <PlatformLogo
              :platform="platform.logo"
              :src="resolveStaticPlatformLogo(platform.logo) ?? ''"
              :alt="t('globalSurface.connection.logoAlt', { platform: platformLabel(platform) })"
              :size="18"
            />
          </span>
        </div>
        <Button
          v-if="section.key === 'reconnectable'"
          variant="outline"
          size="sm"
          @click="health.goToConnections"
        >
          {{ t('globalSurface.connection.authenticate') }}
          <template #end><Icon name="arrow-right" :size="16" aria-hidden="true" /></template>
        </Button>
        <em v-else>{{ contactMessage(section.platforms) }}</em>
      </div>
    </Alert>
  </div>
</template>

<style scoped>
.connection-lost-banner :deep(.ui-alert) {
  border-radius: 0;
}
.connection-lost-bannerem {
  color: var(--ui-color-danger);
  font-size: 0.8125rem;
}
.connection-lost-banner em {
  color: var(--ui-color-danger);
  font-size: 0.8125rem;
}
</style>
